/**
 * Score notification (score notif): achievement unlocks and XP earned.
 * Shown globally wherever the learner earns them; waits while toast notifications animate.
 */

import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Award, Star, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNotifications } from './NotificationSystem';
import { useAppLocale } from '../../providers/LocaleProvider';
import { authService } from '../../services/authService';
import {
  GAMIFICATION_AFTER_ACTION,
  GAMIFICATION_CHECK_ACHIEVEMENTS,
  GAMIFICATION_LEARNER_VIEW,
  GamificationAfterActionDetail,
  isAchievementNew
} from '../../utils/gamificationNotif';

export type ScoreNotifPayload =
  | { type: 'achievement'; achievementId: number; name: string; xpReward: number }
  | { type: 'xp'; amount: number };

interface ScoreNotifItem {
  id: number;
  payload: ScoreNotifPayload;
  createdAt: number;
}

interface ScoreNotifContextType {
  showScoreNotif: (payload: ScoreNotifPayload) => void;
}

const ScoreNotifContext = createContext<ScoreNotifContextType | null>(null);

const XP_AUTO_DISMISS_MS = 3000;
const NAV_HEIGHT = '4rem';
/** Visible width; toast extends past the right edge for a clipped “peek” look. */
const XP_NOTIF_VISIBLE_WIDTH = 'min(30vw, 20rem)';
const XP_PEEK_CLIP_PX = 18;

export function useScoreNotif(): ((payload: ScoreNotifPayload) => void) | undefined {
  const ctx = useContext(ScoreNotifContext);
  return ctx?.showScoreNotif;
}

export const ScoreNotifProvider: React.FC<{
  children: React.ReactNode;
  onGoToMyAchievements?: (achievementId: number) => void;
  /** Current learner view — triggers achievement scan on navigation. */
  learnerView?: string;
}> = ({ children, onGoToMyAchievements, learnerView }) => {
  const { notifications } = useNotifications();
  /** Hold achievement/XP score notifs until success/error toasts are fully gone. */
  const otherSlidingBusy = notifications.length > 0;

  const [queue, setQueue] = useState<ScoreNotifItem[]>([]);
  const [visible, setVisible] = useState<ScoreNotifItem | null>(null);
  const [entering, setEntering] = useState(true);
  const idRef = useRef(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const achievementCheckInFlightRef = useRef(false);
  /** When a check is already running, run again once it finishes (e.g. profile save during nav check). */
  const pendingAchievementCheckRef = useRef(false);
  /** Marked on server after the notif bar was shown. */
  const notifiedAchievementIdsRef = useRef<Set<number>>(new Set());

  const markAchievementNotifiedOnServer = useCallback(async (achievementId: number) => {
    if (notifiedAchievementIdsRef.current.has(achievementId)) return;
    notifiedAchievementIdsRef.current.add(achievementId);
    try {
      await authService.markAchievementsNotified([achievementId]);
    } catch {
      notifiedAchievementIdsRef.current.delete(achievementId);
    }
  }, []);

  const showScoreNotif = useCallback((payload: ScoreNotifPayload) => {
    const id = ++idRef.current;
    const item: ScoreNotifItem = { id, payload, createdAt: Date.now() };
    setQueue((prev) => {
      if (payload.type === 'achievement') {
        const dup = prev.some(
          (q) => q.payload.type === 'achievement' && q.payload.achievementId === payload.achievementId
        );
        if (dup) return prev;
      }
      return [...prev, item];
    });
  }, []);

  const checkAndNotifyAchievements = useCallback(async () => {
    if (!authService.isAuthenticated()) return;
    if (achievementCheckInFlightRef.current) {
      pendingAchievementCheckRef.current = true;
      return;
    }
    achievementCheckInFlightRef.current = true;
    try {
      const data = await authService.getAchievementProgress();
      const toNotify = (data ?? []).filter(
        (i) => isAchievementNew(i) && !notifiedAchievementIdsRef.current.has(i.id)
      );
      if (toNotify.length === 0) return;

      for (const item of toNotify) {
        showScoreNotif({
          type: 'achievement',
          achievementId: item.id,
          name: item.name,
          xpReward: item.xpReward
        });
        const xp = item.xpReward ?? 0;
        if (xp > 0) {
          showScoreNotif({ type: 'xp', amount: xp });
        }
      }
    } catch {
      /* ignore — learner can still open achievements manually */
    } finally {
      achievementCheckInFlightRef.current = false;
      if (pendingAchievementCheckRef.current) {
        pendingAchievementCheckRef.current = false;
        void checkAndNotifyAchievements();
      }
    }
  }, [showScoreNotif]);

  const processQueue = useCallback(() => {
    setEntering(false);
    setTimeout(() => {
      setVisible(null);
      setEntering(true);
      setQueue((prev) => prev.slice(1));
    }, 400);
  }, []);

  const dismissVisible = useCallback(() => {
    if (!visible) return;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    processQueue();
  }, [visible, processQueue]);

  useEffect(() => {
    if (otherSlidingBusy || queue.length === 0 || visible) return;
    setVisible(queue[0]);
    setEntering(true);
  }, [queue, visible, otherSlidingBusy]);

  useEffect(() => {
    if (!visible) return;
    if (visible.payload.type === 'achievement') return;

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      dismissVisible();
      timeoutRef.current = null;
    }, XP_AUTO_DISMISS_MS);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [visible, dismissVisible]);

  /** Mark each achievement notified only once it is actually shown (not when queued). */
  useEffect(() => {
    if (!visible || visible.payload.type !== 'achievement') return;
    void markAchievementNotifiedOnServer(visible.payload.achievementId);
  }, [visible?.id, visible?.payload, markAchievementNotifiedOnServer]);

  useEffect(() => {
    const onCheckAchievements = () => {
      void checkAndNotifyAchievements();
    };
    const onAfterAction = (event: Event) => {
      const detail = (event as CustomEvent<GamificationAfterActionDetail>).detail ?? {};
      const xp = detail.xpEarned;
      if (typeof xp === 'number' && xp > 0) {
        showScoreNotif({ type: 'xp', amount: xp });
      }
      void checkAndNotifyAchievements();
      window.setTimeout(() => void checkAndNotifyAchievements(), 350);
    };
    const onLearnerView = () => {
      void checkAndNotifyAchievements();
    };
    window.addEventListener(GAMIFICATION_AFTER_ACTION, onAfterAction);
    window.addEventListener(GAMIFICATION_CHECK_ACHIEVEMENTS, onCheckAchievements);
    window.addEventListener(GAMIFICATION_LEARNER_VIEW, onLearnerView);
    return () => {
      window.removeEventListener(GAMIFICATION_AFTER_ACTION, onAfterAction);
      window.removeEventListener(GAMIFICATION_CHECK_ACHIEVEMENTS, onCheckAchievements);
      window.removeEventListener(GAMIFICATION_LEARNER_VIEW, onLearnerView);
    };
  }, [showScoreNotif, checkAndNotifyAchievements]);

  useEffect(() => {
    void checkAndNotifyAchievements();
  }, [checkAndNotifyAchievements]);

  useEffect(() => {
    if (!learnerView || learnerView.startsWith('admin')) return;
    void checkAndNotifyAchievements();
  }, [learnerView, checkAndNotifyAchievements]);

  return (
    <ScoreNotifContext.Provider value={{ showScoreNotif }}>
      {children}
      {visible?.payload.type === 'xp' ? (
        <XpScoreNotifToast item={visible} entering={entering} onDismiss={dismissVisible} navHeight={NAV_HEIGHT} />
      ) : (
        <AchievementScoreNotifBar
          item={visible}
          entering={entering}
          onDismiss={dismissVisible}
          navHeight={NAV_HEIGHT}
          onGoToMyAchievements={onGoToMyAchievements}
        />
      )}
    </ScoreNotifContext.Provider>
  );
};

interface AchievementScoreNotifBarProps {
  item: ScoreNotifItem | null;
  entering: boolean;
  onDismiss: () => void;
  navHeight: string;
  onGoToMyAchievements?: (achievementId: number) => void;
}

const AchievementScoreNotifBar: React.FC<AchievementScoreNotifBarProps> = ({
  item,
  entering,
  onDismiss,
  navHeight,
  onGoToMyAchievements
}) => {
  const { t } = useTranslation('notifications');
  const { direction } = useAppLocale();
  const [slideIn, setSlideIn] = React.useState(false);
  React.useEffect(() => {
    if (!item) {
      setSlideIn(false);
      return;
    }
    setSlideIn(false);
    const t = requestAnimationFrame(() => {
      requestAnimationFrame(() => setSlideIn(true));
    });
    return () => cancelAnimationFrame(t);
  }, [item?.id]);

  if (!item || item.payload.type !== 'achievement') return null;

  const barVisible = slideIn && entering;
  const text = t('gamification.achievementUnlocked', {
    name: item.payload.name,
    xp: item.payload.xpReward,
  });

  const bar = (
    <div
      className="fixed left-0 right-0 z-[2147482000] pointer-events-none"
      style={{ top: navHeight }}
      role="status"
      aria-live="polite"
      data-app-score-notif-layer
    >
      <div
        className={`
          flex items-start sm:items-center justify-between gap-2 sm:gap-3 px-3 sm:px-4 py-2 pointer-events-auto min-h-[2.75rem]
          bg-gradient-to-r from-amber-500/95 to-yellow-500/95 dark:from-amber-600/95 dark:to-yellow-600/95
          text-gray-900 dark:text-gray-100 font-medium text-sm sm:text-base shadow-lg
          border-b border-amber-600/30 dark:border-amber-500/30
          transition-transform duration-300 ease-out
        `}
        style={{
          transform: barVisible ? 'translateX(0)' : entering ? 'translateX(100%)' : 'translateX(-100%)'
        }}
      >
        <div className="flex items-start gap-2 min-w-0 flex-1 pt-0.5 sm:pt-0">
          <Award className="w-5 h-5 flex-shrink-0 text-amber-800 dark:text-amber-200 mt-0.5 sm:mt-0" />
          <span
            className={`break-words leading-snug ${direction === 'rtl' ? 'text-right' : 'text-left'}`}
          >
            {text}
          </span>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0 self-start sm:self-center">
          {onGoToMyAchievements && (
            <button
              type="button"
              onClick={() => {
                onGoToMyAchievements(item.payload.achievementId);
                onDismiss();
              }}
              className="px-2.5 py-1.5 rounded-lg text-sm font-semibold underline underline-offset-2 hover:bg-amber-600/30 dark:hover:bg-amber-500/30 transition-colors whitespace-nowrap"
            >
              {t('gamification.showAchievement')}
            </button>
          )}
          <button
            type="button"
            onClick={onDismiss}
            className="p-1.5 rounded-lg hover:bg-amber-600/30 dark:hover:bg-amber-500/30 transition-colors"
            aria-label={t('gamification.dismiss')}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );

  if (typeof document === 'undefined') return bar;
  return createPortal(bar, document.body);
};

interface XpScoreNotifToastProps {
  item: ScoreNotifItem;
  entering: boolean;
  onDismiss: () => void;
  navHeight: string;
}

const XpScoreNotifToast: React.FC<XpScoreNotifToastProps> = ({ item, entering, onDismiss, navHeight }) => {
  const { t } = useTranslation('notifications');
  const [slideIn, setSlideIn] = React.useState(false);
  const [exiting, setExiting] = React.useState(false);

  React.useEffect(() => {
    setSlideIn(false);
    setExiting(false);
    const t = requestAnimationFrame(() => {
      requestAnimationFrame(() => setSlideIn(true));
    });
    return () => cancelAnimationFrame(t);
  }, [item.id]);

  React.useEffect(() => {
    if (!entering) {
      setExiting(true);
    }
  }, [entering]);

  if (item.payload.type !== 'xp') return null;

  const offScreenRight = 'translateX(100%)';
  const transform = exiting ? offScreenRight : slideIn ? 'translateX(0)' : offScreenRight;

  const toast = (
    <div
      className="fixed z-[2147482000] pointer-events-none overflow-hidden"
      style={{
        top: navHeight,
        right: 0,
        width: XP_NOTIF_VISIBLE_WIDTH,
        minWidth: '10rem'
      }}
      role="status"
      aria-live="polite"
      data-app-score-notif-layer
    >
      <div
        className={`
          pointer-events-auto flex items-center gap-2 pl-3 pr-4 py-2 rounded-l-lg rounded-r-none shadow-lg
          bg-gradient-to-r from-amber-500/95 to-yellow-500/95 dark:from-amber-600/95 dark:to-yellow-600/95
          text-gray-900 dark:text-gray-100 font-medium text-sm border border-amber-600/30 dark:border-amber-500/30 border-r-0
          transition-transform duration-300 ease-out
        `}
        style={{
          transform,
          width: `calc(100% + ${XP_PEEK_CLIP_PX}px)`
        }}
      >
        <Star className="w-4 h-4 flex-shrink-0 text-amber-800 dark:text-amber-200" />
        <span className="leading-snug whitespace-nowrap">
          {t('gamification.xpEarned', { amount: item.payload.amount })}
        </span>
      </div>
    </div>
  );

  if (typeof document === 'undefined') return toast;
  return createPortal(toast, document.body);
};
